// İSG uzmanı portalı — Ziyaretçi girişi (check-in) formu (Modal + RHF + Zod). api-contract
// §11.1 (`POST /visits/check-in`). Ziyaretçi bilgileri + saha/şube seçici (Ayarlar §10
// `GET /sites`/`GET /branches` — mevcut USelect) + amaç + **KVKK açık rıza (ZORUNLU)** +
// **güvenlik brifing onayı (ZORUNLU)**. İki onay verilmeden gönder butonu pasiftir
// (backend de dayatır: `KVKK_CONSENT_REQUIRED` / `SAFETY_BRIEFING_REQUIRED`). Başarıda
// üretilen çıkış kodu (badgeCode) gösterilir.
//
// Ev sahibi (host) seçici: api-contract §11 `hostUserUuid` **opsiyoneldir**; firma
// kullanıcı listesi artık §12 (`GET /users`) ile mevcuttur. Seçici bu uçtan dolar,
// FE tarafında `isActive === true` süzülür (§12 aktiflik kararı) ve kanonik selectlist
// shape'ine (`{ value: uuid, label: "firstName lastName" }`) map'lenir. Alan opsiyoneldir:
// seçilmezse `hostUserUuid` **gönderilmez** (undefined — boş string DEĞİL). Backend host'u
// bağımsızca aynı-firma + aktif ile doğrular (yoksa `404 HOST_NOT_FOUND`).

import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  Button,
  Field,
  FormSection,
  Input,
  Modal,
  Select,
  type SelectOption,
  Spinner,
  type VisitDto,
  currentCulture,
  resolveApiErrorMessage,
  settingsApi,
  usersApi,
  visitsApi,
} from '@isgportal/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { settingsKeys } from '../../lib/settingsKeys';
import { usersKeys } from '../../lib/usersKeys';
import { visitsKeys } from '../../lib/visitsKeys';

/** Saha/şube seçicilerini tek sayfada besleyecek makul üst sınır (tenant ölçeği). */
const OPTIONS_PAGE_SIZE = 200;

interface CheckInModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

/** Boş/whitespace dizeyi `null`'a indirger (opsiyonel alanlar backend'e `null` gider). */
function nullable(value: string | undefined): string | null {
  return value && value.trim().length > 0 ? value.trim() : null;
}

/** Boş dizeyi `undefined`'a indirger (opsiyonel saha/şube — gönderilmez). */
function orUndefined(value: string | undefined): string | undefined {
  return value && value.length > 0 ? value : undefined;
}

export function CheckInModal({ open, onOpenChange, onSuccess }: CheckInModalProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const culture = currentCulture();
  const [createdVisit, setCreatedVisit] = useState<VisitDto | null>(null);

  // Saha + şube seçenekleri — yalnız modal açıkken çekilir (kanonik seçim shape).
  const sitesQuery = useQuery({
    queryKey: settingsKeys.sites({
      page: 1,
      pageSize: OPTIONS_PAGE_SIZE,
      sortBy: 'name',
      sortDir: 'asc',
    }),
    queryFn: () =>
      settingsApi.listSites({
        page: 1,
        pageSize: OPTIONS_PAGE_SIZE,
        sortBy: 'name',
        sortDir: 'asc',
      }),
    enabled: open,
    staleTime: 60_000,
  });
  const branchesQuery = useQuery({
    queryKey: settingsKeys.branchOptions(),
    queryFn: () =>
      settingsApi.listBranches({
        page: 1,
        pageSize: OPTIONS_PAGE_SIZE,
        sortBy: 'name',
        sortDir: 'asc',
      }),
    enabled: open,
    staleTime: 60_000,
  });

  // Ev sahibi (host) seçenekleri — firma kullanıcıları (§12 `GET /users`). Yalnız modal
  // açıkken çekilir; aktif kullanıcılar FE'de süzülür (§12 aktiflik kararı).
  const usersQuery = useQuery({
    queryKey: usersKeys.hostOptions({ page: 1, pageSize: OPTIONS_PAGE_SIZE }),
    queryFn: () => usersApi.listUsers({ page: 1, pageSize: OPTIONS_PAGE_SIZE }),
    enabled: open,
    staleTime: 60_000,
  });

  // Aktif güvenlik brifingi — gate ile aynı anahtarı paylaşır (çoğunlukla cache'ten).
  const briefingQuery = useQuery({
    queryKey: visitsKeys.safetyBriefing(),
    queryFn: () => visitsApi.getSafetyBriefing(),
    enabled: open,
    staleTime: 60_000,
  });

  const siteOptions: SelectOption[] = useMemo(
    () => (sitesQuery.data?.items ?? []).map((s) => ({ value: s.uuid, label: s.name })),
    [sitesQuery.data],
  );
  const branchOptions: SelectOption[] = useMemo(
    () => (branchesQuery.data?.items ?? []).map((b) => ({ value: b.uuid, label: b.name })),
    [branchesQuery.data],
  );
  // Ev sahibi seçenekleri — yalnız aktif kullanıcılar (§12), kanonik shape. KVKK/PII:
  // etikette yalnız ad-soyad; e-posta gösterilmez (disambiguation gerekmez — tercih).
  const hostOptions: SelectOption[] = useMemo(
    () =>
      (usersQuery.data?.items ?? [])
        .filter((u) => u.isActive === true)
        .map((u) => ({ value: u.uuid, label: `${u.firstName} ${u.lastName}` })),
    [usersQuery.data],
  );

  const briefing = briefingQuery.data;
  const briefingText = useMemo(() => {
    if (!briefing) {
      return '';
    }
    if (culture.startsWith('en')) {
      return briefing.contentEn || briefing.contentTr;
    }
    if (culture.startsWith('de')) {
      return briefing.contentDe || briefing.contentTr;
    }
    return briefing.contentTr;
  }, [briefing, culture]);
  const briefingConfigured = Boolean(
    briefing && briefing.version > 0 && briefingText.trim().length > 0,
  );

  const schema = useMemo(
    () =>
      z.object({
        firstName: z.string().trim().min(1, t('visitors.validation.firstNameRequired')),
        lastName: z.string().trim().min(1, t('visitors.validation.lastNameRequired')),
        phone: z.string().trim().optional(),
        email: z.string().trim().optional(),
        idNumber: z.string().trim().optional(),
        organization: z.string().trim().optional(),
        siteUuid: z.string().optional(),
        branchUuid: z.string().optional(),
        // Ev sahibi opsiyonel (§11 `hostUserUuid`); boş bırakılırsa gönderilmez.
        hostUserUuid: z.string().optional(),
        purpose: z.string().trim().optional(),
        // KVKK açık rıza + güvenlik brifing onayı — ikisi de ZORUNLU (backend dayatır).
        kvkkConsent: z.boolean().refine((v) => v === true, {
          message: t('visitors.validation.kvkkRequired'),
        }),
        safetyBriefingAccepted: z.boolean().refine((v) => v === true, {
          message: t('visitors.validation.briefingRequired'),
        }),
      }),
    [t],
  );
  type FormValues = z.infer<typeof schema>;

  const defaults: FormValues = {
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    idNumber: '',
    organization: '',
    siteUuid: '',
    branchUuid: '',
    hostUserUuid: '',
    purpose: '',
    kvkkConsent: false,
    safetyBriefingAccepted: false,
  };

  const { register, handleSubmit, reset, watch, setValue, formState } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: defaults,
  });

  // Onaysız gönderim yok: iki onay işaretlenmeden gönder butonu pasif (UX kapısı).
  const kvkkChecked = watch('kvkkConsent');
  const briefingChecked = watch('safetyBriefingAccepted');
  const consentComplete = kvkkChecked === true && briefingChecked === true;

  // Saha → şube otomatik doldurma (S36 #13). `SiteSettingsDto.branchUuid` HER ZAMAN doludur
  // (bir saha tam olarak bir şubeye aittir, api-contract §10.3) — bu yüzden koşulsuz uygulanır;
  // şube seçici seçili bir saha varken salt-okunur olur (elle çelişen bir değer girilemez).
  const selectedSiteUuid = watch('siteUuid');
  const selectedSite = useMemo(
    () => (sitesQuery.data?.items ?? []).find((s) => s.uuid === selectedSiteUuid) ?? null,
    [sitesQuery.data, selectedSiteUuid],
  );
  useEffect(() => {
    if (selectedSite) {
      setValue('branchUuid', selectedSite.branchUuid, { shouldValidate: true });
    }
  }, [selectedSite, setValue]);

  const mutation = useMutation({
    mutationFn: (values: FormValues) =>
      visitsApi.checkIn({
        visitor: {
          firstName: values.firstName.trim(),
          lastName: values.lastName.trim(),
          phone: nullable(values.phone),
          email: nullable(values.email),
          idNumber: nullable(values.idNumber),
          organization: nullable(values.organization),
        },
        siteUuid: orUndefined(values.siteUuid),
        branchUuid: orUndefined(values.branchUuid),
        // Opsiyonel host — seçilmezse `undefined` (boş string DEĞİL) → istekte gönderilmez.
        hostUserUuid: orUndefined(values.hostUserUuid),
        purpose: nullable(values.purpose),
        kvkkConsent: true,
        safetyBriefingAccepted: true,
      }),
    onSuccess: (visit) => {
      queryClient.invalidateQueries({ queryKey: visitsKeys.list() });
      setCreatedVisit(visit);
      reset(defaults);
      onSuccess?.();
    },
  });

  function closeAll(next: boolean) {
    if (!next) {
      mutation.reset();
      setCreatedVisit(null);
      reset(defaults);
    }
    onOpenChange(next);
  }

  const checkbox = 'mt-0.5 h-4 w-4 shrink-0 accent-[var(--color-primary)]';

  // ---- Başarı görünümü: üretilen çıkış kodu (badgeCode) gösterilir ----
  if (createdVisit) {
    return (
      <Modal
        open={open}
        onOpenChange={closeAll}
        title={t('visitors.checkIn.success')}
        closeLabel={t('visitors.common.close')}
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => {
                setCreatedVisit(null);
                mutation.reset();
              }}
            >
              {t('visitors.onSite.checkIn')}
            </Button>
            <Button onClick={() => closeAll(false)}>{t('visitors.common.close')}</Button>
          </>
        }
      >
        <div className="flex flex-col items-center gap-4 py-2 text-center">
          <CheckCircle2 className="h-10 w-10 text-success" aria-hidden="true" />
          <p className="text-sm text-fg">
            {createdVisit.visitor.firstName} {createdVisit.visitor.lastName}
          </p>
          {createdVisit.badgeCode ? (
            <Alert tone="success">
              {t('visitors.checkIn.badge', { code: createdVisit.badgeCode })}
            </Alert>
          ) : null}
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      open={open}
      onOpenChange={closeAll}
      title={t('visitors.checkIn.title')}
      description={t('visitors.checkIn.description')}
      size="lg"
      closeLabel={t('visitors.common.close')}
      footer={
        <>
          <Button variant="secondary" onClick={() => closeAll(false)} disabled={mutation.isPending}>
            {t('visitors.common.cancel')}
          </Button>
          <Button
            type="submit"
            form="checkin-form"
            loading={mutation.isPending}
            disabled={!consentComplete}
          >
            {mutation.isPending ? (
              <Spinner label={t('visitors.checkIn.submit')} />
            ) : (
              t('visitors.checkIn.submit')
            )}
          </Button>
        </>
      }
    >
      <form
        id="checkin-form"
        className="flex flex-col gap-8"
        onSubmit={handleSubmit((values) => mutation.mutate(values))}
        noValidate
      >
        {mutation.isError ? (
          <Alert tone="danger">{resolveApiErrorMessage(mutation.error, t)}</Alert>
        ) : null}

        {/* Ziyaretçi bilgileri */}
        <FormSection legend={t('visitors.checkIn.section.visitor')}>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field
              htmlFor="checkin-first"
              label={t('visitors.checkIn.field.firstName')}
              error={formState.errors.firstName?.message}
              required
            >
              <Input
                id="checkin-first"
                invalid={Boolean(formState.errors.firstName)}
                {...register('firstName')}
              />
            </Field>
            <Field
              htmlFor="checkin-last"
              label={t('visitors.checkIn.field.lastName')}
              error={formState.errors.lastName?.message}
              required
            >
              <Input
                id="checkin-last"
                invalid={Boolean(formState.errors.lastName)}
                {...register('lastName')}
              />
            </Field>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field htmlFor="checkin-phone" label={t('visitors.checkIn.field.phone')}>
              <Input id="checkin-phone" type="tel" {...register('phone')} />
            </Field>
            <Field htmlFor="checkin-email" label={t('visitors.checkIn.field.email')}>
              <Input id="checkin-email" type="email" {...register('email')} />
            </Field>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field htmlFor="checkin-id" label={t('visitors.checkIn.field.idNumber')}>
              <Input id="checkin-id" {...register('idNumber')} />
            </Field>
            <Field htmlFor="checkin-org" label={t('visitors.checkIn.field.organization')}>
              <Input id="checkin-org" {...register('organization')} />
            </Field>
          </div>
        </FormSection>

        {/* Ziyaret bilgileri — saha/şube seçici (opsiyonel) + amaç */}
        <FormSection legend={t('visitors.checkIn.section.visit')}>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field htmlFor="checkin-site" label={t('visitors.checkIn.field.site')}>
              <Select
                id="checkin-site"
                disabled={sitesQuery.isPending}
                placeholder={t('visitors.checkIn.site.placeholder')}
                options={siteOptions}
                {...register('siteUuid')}
              />
            </Field>
            <Field
              htmlFor="checkin-branch"
              label={t('visitors.checkIn.field.branch')}
              hint={selectedSite ? t('visitors.checkIn.branch.autoFilledHint') : undefined}
            >
              <Select
                id="checkin-branch"
                disabled={branchesQuery.isPending || Boolean(selectedSite)}
                placeholder={t('visitors.checkIn.branch.placeholder')}
                options={branchOptions}
                {...register('branchUuid')}
              />
            </Field>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field htmlFor="checkin-host" label={t('visitors.checkIn.field.host')}>
              <Select
                id="checkin-host"
                disabled={usersQuery.isPending}
                placeholder={t('visitors.checkIn.host.placeholder')}
                options={hostOptions}
                {...register('hostUserUuid')}
              />
            </Field>
            <Field htmlFor="checkin-purpose" label={t('visitors.checkIn.field.purpose')}>
              <Input id="checkin-purpose" {...register('purpose')} />
            </Field>
          </div>
        </FormSection>

        {/* Onaylar — KVKK + güvenlik brifingi (ikisi de zorunlu) */}
        {/* FormSection'ın `fieldset` variant'ı üst dolguyu kendisi taşır (`pt-4` — bkz.
            FormSection.tsx); burada yalnız yatay/alt dolgu + kutulu görünüm eklenir, `p-4`/`pt-*`
            VERİLMEZ (aksi halde legend→ilk-çocuk boşluğu 32px'e katlanır). */}
        <FormSection
          legend={t('visitors.checkIn.section.consent')}
          className="rounded-md border border-border bg-surface-muted/40 px-4 pb-4"
          legendClassName="px-1"
        >
          {/* Güvenlik brifingi metni (aktif sürüm) */}
          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between gap-2">
              <span className="text-sm font-medium text-fg">
                {t('visitors.checkIn.briefing.title')}
              </span>
              {briefingConfigured ? (
                <span className="rounded bg-surface px-1.5 py-0.5 text-xs text-muted">
                  {t('visitors.checkIn.briefing.version', { version: briefing?.version ?? 0 })}
                </span>
              ) : null}
            </div>
            {briefingQuery.isPending ? (
              <Spinner label={t('visitors.common.loading')} />
            ) : briefingConfigured ? (
              <div className="max-h-40 overflow-y-auto whitespace-pre-wrap rounded-md border border-border bg-surface p-3 text-sm text-fg">
                {briefingText}
              </div>
            ) : (
              <Alert tone="info">{t('visitors.checkIn.briefing.notConfigured')}</Alert>
            )}
          </div>

          <label className="flex items-start gap-2 text-sm text-fg">
            <input type="checkbox" className={checkbox} {...register('safetyBriefingAccepted')} />
            <span>{t('visitors.checkIn.consent.briefing')}</span>
          </label>

          <label className="flex items-start gap-2 text-sm text-fg">
            <input type="checkbox" className={checkbox} {...register('kvkkConsent')} />
            <span className="flex flex-col gap-0.5">
              <span>{t('visitors.checkIn.consent.kvkk')}</span>
              <span className="text-xs text-muted">{t('visitors.checkIn.consent.kvkkHint')}</span>
            </span>
          </label>
        </FormSection>
      </form>
    </Modal>
  );
}
